import type { Scope } from './domain.ts';

export const PLAN_IDS = ['free', 'plus', 'premium'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export interface Plan {
  id: PlanId;
  name: string;
  /** Monatspreis in Cent. Daraus legt der Server die Stripe-Preise an – nur hier ändern. */
  priceCents: number;
  /** Kostenlose Testphase in Tagen, 0 = keine. */
  trialDays: number;
  scopes: readonly Scope[];
  genderFilter: boolean;
  highlights: readonly string[];
}

/**
 * Tarifmatrix – die einzige Quelle für Freischaltungen.
 * Der Server erzwingt sie beim Matching, der Browser nutzt sie nur für die Anzeige.
 */
export const PLANS: Readonly<Record<PlanId, Plan>> = {
  free: {
    id: 'free',
    name: 'Free',
    priceCents: 0,
    trialDays: 0,
    scopes: ['global', 'national'],
    genderFilter: false,
    highlights: ['Zufällige Videochats', 'Weltweit oder in deinem Land'],
  },
  plus: {
    id: 'plus',
    name: 'Plus',
    priceCents: 299,
    trialDays: 3,
    scopes: ['global', 'national', 'local'],
    genderFilter: false,
    highlights: ['Alles aus Free', 'Leute in deiner Nähe, zum Beispiel in 40 km'],
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    priceCents: 599,
    trialDays: 3,
    scopes: ['global', 'national', 'local'],
    genderFilter: true,
    highlights: ['Alles aus Plus', 'Geschlechterfilter', 'Kombinierbar mit dem Umkreis'],
  },
};

export function canUseScope(plan: PlanId, scope: Scope): boolean {
  return PLANS[plan].scopes.includes(scope);
}

export function canUseGenderFilter(plan: PlanId): boolean {
  return PLANS[plan].genderFilter;
}

/** Günstigster Tarif, der eine Suche mit diesen Filtern erlaubt. */
export function requiredPlan(scope: Scope, genderFiltered: boolean): PlanId {
  const plan = PLAN_IDS.find(
    (id) => canUseScope(id, scope) && (!genderFiltered || canUseGenderFilter(id)),
  );
  return plan ?? 'premium';
}

export function formatPrice(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

/** Tarife, die es als Abo gibt. */
export const PAID_PLAN_IDS = ['plus', 'premium'] as const;
export type PaidPlanId = (typeof PAID_PLAN_IDS)[number];

/** Was der Browser über die Zahlungsabwicklung wissen darf – bewusst ohne Schlüssel. */
export interface BillingInfo {
  /** test = Stripe-Testmodus, es fließt kein echtes Geld */
  mode: 'test' | 'live';
  /** Muss für die Gratis-Testphase ein Zahlungsmittel hinterlegt werden? */
  trialRequiresCard: boolean;
}

/** Stand eines Abos für die Anzeige. Zeiten in Millisekunden. */
export interface SubscriptionInfo {
  plan: PaidPlanId;
  /** Stripe-Status, z. B. trialing, active, past_due, canceled */
  status: string;
  trialEnd: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
}

/** Antwort der Abo-Endpunkte: gültiger Tarif und – falls freigeschaltet – ein neues Token. */
export interface EntitlementResponse {
  plan: PlanId;
  token: string | null;
  expiresAt: number | null;
  subscription: SubscriptionInfo | null;
}
